import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { allArticles } from '@/mocks/laceData';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = allArticles.find((a) => a.slug === slug);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} | 蕾丝雅集 Lace Elegance`;
    }
  }, [article]);

  if (!article) {
    return (
      <div className="pt-[72px] min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-2xl text-foreground-900 mb-4">文章未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 cursor-pointer">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    image: article.image,
    author: {
      '@type': 'Organization',
      name: '蕾丝雅集',
    },
    publisher: {
      '@type': 'Organization',
      name: '蕾丝雅集',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': window.location.origin + __BASE_PATH__ + `/article/${article.slug}`,
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: window.location.origin + __BASE_PATH__ + '/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: article.category,
          item: window.location.origin + __BASE_PATH__ + `/category/${article.categorySlug}`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: article.title,
        },
      ],
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <div className="pt-[72px]">
        <div className="mx-auto px-4 md:px-6" style={{ maxWidth: '1280px' }}>
          <Breadcrumb
            items={[
              { label: article.category, href: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />
        </div>

        <article className="mx-auto px-4 md:px-6 pb-20" style={{ maxWidth: '900px' }}>
          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Link
                to={`/category/${article.categorySlug}`}
                className="text-xs text-primary-500 bg-primary-100 px-3 py-1 rounded-full cursor-pointer"
              >
                {article.category}
              </Link>
              <span className="text-xs text-foreground-500">{article.date}</span>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-900 mb-6 leading-tight">
              {article.title}
            </h1>
            <div className="h-[350px] md:h-[450px] rounded-lg overflow-hidden mb-6">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </header>

          <div className="prose-custom text-foreground-800 text-sm md:text-base leading-relaxed space-y-6">
            <p className="text-lg text-foreground-700 leading-relaxed">
              {article.excerpt}
            </p>
            <p>
              蕾丝作为时尚界最为精致的面料之一，其历史可以追溯到16世纪的欧洲。从最初的手工编织，到如今的机械化生产，蕾丝工艺经历了数百年的演变，但那份与生俱来的优雅与浪漫从未改变。
            </p>
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mt-10 mb-4">
              历史渊源与文化传承
            </h2>
            <p>
              蕾丝的起源可以追溯到文艺复兴时期的意大利和佛兰德斯地区。当时，蕾丝被视为贵族身份的象征，只有皇室成员和上层社会才能享用。法国国王路易十四对蕾丝情有独钟，在他的推动下，法国成为欧洲蕾丝生产的中心，诞生了尚蒂伊蕾丝、阿朗松蕾丝等享誉世界的经典品种。
            </p>
            <p>
              到了19世纪，随着工业革命的到来，蕾丝生产从手工制作逐渐转向机械化。虽然机器蕾丝在效率上远超手工，但顶级手工蕾丝的价值反而因为稀缺而更加珍贵。直到今天，一件由匠人花费数月手工编织的蕾丝作品，依然是时尚界最为珍视的瑰宝。
            </p>
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mt-10 mb-4">
              工艺特征与鉴别要点
            </h2>
            <p>
              优质蕾丝具备几个显著特征：首先是图案的清晰度和对称性，好的蕾丝图案边缘锐利、线条流畅，没有模糊或断裂；其次是面料的柔软度和悬垂性，顶级蕾丝触感柔滑细腻，能够自然贴合身体曲线；第三是背面整洁度，真正的精品蕾丝背面同样干净利落，没有多余线头和杂乱的连接。
            </p>
            <p>
              在选购蕾丝产品时，建议从以下几个方面进行判断：观察蕾丝在光线下的色泽和通透感；用手触摸感受面料的柔软度和厚度；翻转查看背面工艺是否精细；最后试穿感受整体舒适度。记住，真正优质的蕾丝，即使贴身穿着一整天也不会感到刺痒或不适。
            </p>
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mt-10 mb-4">
              现代时尚中的蕾丝应用
            </h2>
            <p>
              在当代时尚界，蕾丝早已不再是单一的装饰元素，而是成为了设计师表达创意的重要媒介。从高级定制的全蕾丝礼服，到成衣中的蕾丝拼接和点缀，再到配饰和家居装饰中的创意运用，蕾丝的应用场景不断拓展，展现出令人惊叹的多样性。
            </p>
            <p>
              特别值得关注的是，越来越多的年轻设计师开始将蕾丝与当代街头风格、运动元素相结合，创造出既保留了蕾丝优雅气质，又充满现代感和活力的全新美学。这种传统与现代的碰撞，让蕾丝焕发出前所未有的生命力。
            </p>
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mt-10 mb-4">
              保养与收藏建议
            </h2>
            <p>
              蕾丝的保养需要格外用心。清洗时使用温水和中性洗涤剂，手洗轻柔按压，切勿机洗或用力搓揉。晾晒时避免阳光直射，最好平铺在通风处自然风干。长期不穿着的蕾丝衣物，建议使用无酸纸包裹后放入透气的收纳袋中，避免折叠产生永久压痕。如果条件允许，可以定期将蕾丝衣物送至专业干洗店进行保养。
            </p>
            <blockquote className="border-l-4 border-primary-400 pl-5 py-2 my-6 italic text-foreground-600">
              "蕾丝是时光的织锦，每一寸都凝结着匠人的心血与智慧。它不仅仅是面料，更是一种传承——连接着过去与未来，东方与西方的美学纽带。"
            </blockquote>
            <p>
              无论你是蕾丝的资深爱好者，还是刚刚开始探索这一精致世界的初学者，我们希望这篇文章能为你提供有价值的参考。蕾丝的魅力在于它的细腻与永恒——它不会因为流行趋势的更替而褪色，而是随着岁月的沉淀愈发珍贵。
            </p>
          </div>

          <div className="mt-12 pt-8 border-t border-background-200/70">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <Link
                to={`/category/${article.categorySlug}`}
                className="text-sm text-primary-500 hover:text-primary-600 flex items-center gap-1 cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i> 返回{article.category}
              </Link>
              <div className="flex gap-3">
                <span className="text-xs text-foreground-500">分享：</span>
                <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-500 hover:text-background-50 transition-all duration-200 cursor-pointer">
                  <i className="ri-wechat-line"></i>
                </a>
                <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-500 hover:text-background-50 transition-all duration-200 cursor-pointer">
                  <i className="ri-weibo-line"></i>
                </a>
                <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-500 hover:text-background-50 transition-all duration-200 cursor-pointer">
                  <i className="ri-link-m"></i>
                </a>
              </div>
            </div>
          </div>
        </article>
      </div>
    </>
  );
}